// src/admin/components/AdminSidebar.tsx
import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ImageIcon from "@mui/icons-material/Image";
import CampaignIcon from "@mui/icons-material/Campaign";
import ArticleIcon from "@mui/icons-material/Article";
import CollectionsIcon from "@mui/icons-material/Collections";
import ReviewsIcon from "@mui/icons-material/Reviews";
import QuizIcon from "@mui/icons-material/Quiz";

type Props = {
  width: number;
  mobileOpen: boolean;
  onClose: () => void;
};

const AdminSidebar: React.FC<Props> = ({ width, mobileOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const items = [
    {
      label: "Dashboard",
      icon: <DashboardIcon />,
      path: "/admin",
    },
    {
      label: "Products",
      icon: <Inventory2Icon />,
      path: "/admin/products",
    },
    {
      label: "Campaigns",
      icon: <LocalOfferIcon />, // promotions / offers
      path: "/admin/campaigns",
    },
    {
      label: "Hero Section",
      icon: <ViewCarouselIcon />, // homepage hero / slider
      path: "/admin/hero",
    },
    {
      label: "Newsletter Users",
      icon: <PeopleAltIcon />,
      path: "/admin/newsletter-users",
    },
    {
      label: "Newsletter Banner",
      icon: <ImageIcon />,
      path: "/admin/newsletter-banner",
    },
    {
      label: "Announcement Banner",
      icon: <CampaignIcon />, // announcements / broadcasts
      path: "/admin/announcement-banner",
    },
    {
      label: "Blogs",
      icon: <ArticleIcon />, // articles / posts
      path: "/admin/blogs",
    },
    {
      label: "Collections",
      icon: <CollectionsIcon />, // grouped products
      path: "/admin/collections",
    },
    {
      label: "Google Reviews",
      icon: <ReviewsIcon />,
      path: "/admin/google-reviews",
    },
    {
      label: "Homepage FAQs",
      icon: <QuizIcon />,
      path: "/admin/faqs/home",
    },
    {
      label: "Listing FAQs",
      icon: <QuizIcon />,
      path: "/admin/faqs/listing",
    },
    {
      label: "Listing Intros",
      icon: <ArticleIcon />,
      path: "/admin/listing-intros",
    },
  ];

  const drawerContent = (
    <>
      <Toolbar>
        <Typography variant="h6" noWrap>
          HS Admin
        </Typography>
      </Toolbar>
      <Box sx={{ overflow: "auto" }}>
        <List>
          {items.map((item) => (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (!isDesktop) onClose();
              }}
            >
              <ListItemIcon sx={{ color: "#fff" }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </>
  );

  return (
    <>
      {/* Desktop permanent drawer */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          open
          sx={{
            width,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width,
              boxSizing: "border-box",
              bgcolor: "#111",
              color: "#fff",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile temporary drawer */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: "80%",
              maxWidth: width,
              boxSizing: "border-box",
              bgcolor: "#111",
              color: "#fff",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default AdminSidebar;
